/**
 * Leadler › Harita and Huni view model. The shapes follow the database read models the
 * API will serve (packages/db/src/growth.ts: cellMap over growth.v_cell_map and leadFunnel),
 * with instants as epoch milliseconds like the rest of the panel view model. Company-level
 * counts only: no company names, contacts or buyer data on either screen.
 *
 * Panel-side until the API phase moves them to @twin/contracts.
 */

import type { Instant } from './types.js';

export const CELL_STATUSES = ['taranmadi', 'taraniyor', 'tamamlandi', 'kismi'] as const;
export type CellStatus = (typeof CELL_STATUSES)[number];

export const CELL_CLASSES = [
  'industrial',
  'mixed',
  'commercial',
  'residential',
  'rural',
  'empty_or_water',
  'unknown',
] as const;
export type CellClass = (typeof CELL_CLASSES)[number];

export const CELL_STATUS_LABELS: Readonly<Record<CellStatus, string>> = Object.freeze({
  taranmadi: 'Taranmadı',
  taraniyor: 'Taranıyor',
  kismi: 'Kısmi',
  tamamlandi: 'Tamamlandı',
});

export const CELL_CLASS_LABELS: Readonly<Record<CellClass, string>> = Object.freeze({
  industrial: 'Sanayi',
  mixed: 'Karma',
  commercial: 'Ticari',
  residential: 'Konut',
  rural: 'Kırsal',
  empty_or_water: 'Boş veya su',
  unknown: 'Bilinmiyor',
});

export interface BoundingBox {
  minLat: number;
  minLon: number;
  maxLat: number;
  maxLon: number;
}

/** One scan cell (growth.v_cell_map). */
export interface LeadMapCell {
  cellPk: number;
  grid: 'h3' | 'geohash';
  cellId: string;
  resolution: number;
  countryCode: string | null;
  region: string | null;
  /** Suburbs the cell covers. */
  localities: string[];
  status: CellStatus;
  cellClass: CellClass;
  bbox: BoundingBox;
  centroid: { lat: number; lon: number };
  plannedOrder: number | null;
  lastScannedAt: Instant | null;
  nextScanAt: Instant | null;
  /** The cell's first scan run: the dashboard numbers scanned cells in this order. */
  firstScannedAt: Instant | null;
  queriesRun: number;
  newQualified: number;
  /** Raw results the queries returned. */
  resultsSeen: number;
  /** Candidates that became a company row. */
  companiesResolved: number;
  /** Queries since the last new qualified company, against satK (the saturation rule). */
  queriesSinceLastNew: number;
  satK: number;
  costMicroUsd: number;
  scanMode: string;
  /** The outline as [lat, lon] pairs, closed: the H3 hexagon or the geohash box. */
  boundary: [number, number][];
  sites: number;
  leads: number;
  qualifiedLeads: number;
  customers: number;
}

export const SCAN_MODE_LABELS: Readonly<Record<string, string>> = Object.freeze({
  full: 'Tam tarama',
  full_after_pilot: 'Pilot sonrası tam',
  region_queries_plus_osm: 'Bölge sorguları + OSM',
  probe_then_skip: 'Sonda, sonra atla',
});

export const RUN_STATUS_LABELS: Readonly<Record<string, string>> = Object.freeze({
  running: 'Çalışıyor',
  completed: 'Tamamlandı',
  partial: 'Kısmi',
  failed: 'Düştü',
  aborted: 'Durduruldu',
});

export const STOP_REASON_LABELS: Readonly<Record<string, string>> = Object.freeze({
  saturated: 'Doydu',
  max_queries: 'Sorgu tavanı',
  all_planned_done: 'Plan bitti',
  budget: 'Bütçe',
  credit_floor: 'Kredi tabanı',
  kill_switch: 'Acil durdurma',
  error: 'Hata',
  manual: 'Elle',
  timeout: 'Süre doldu',
});

export const QUERY_STATUS_LABELS: Readonly<Record<string, string>> = Object.freeze({
  ok: 'Sonuç var',
  empty: 'Boş',
  error: 'Hata',
  rate_limited: 'Hız sınırı',
  blocked: 'Engellendi',
});

export const QUERY_FAMILY_LABELS: Readonly<Record<string, string>> = Object.freeze({
  A: 'Ürün × yer',
  B: 'Araç × iş türü',
  C: 'Serbest',
  osm: 'OSM',
  directory: 'Rehber',
  apollo: 'Apollo',
});

export const PROVIDER_LABELS: Readonly<Record<string, string>> = Object.freeze({
  brave: 'Brave',
  serper: 'Serper',
  overpass: 'OSM Overpass',
  places: 'Google Places',
  here: 'HERE',
  gemini: 'Genesis Eye araması',
});

/** One scan run of a cell (growth.scan_runs). */
export interface CellRun {
  scanRunId: number;
  status: string;
  startedAt: Instant;
  finishedAt: Instant | null;
  plannedQueries: number;
  processed: number;
  remaining: number | null;
  errors: number;
  resultsSeen: number;
  newCompanies: number;
  newQualified: number;
  stopReason: string | null;
  costMicroUsd: number;
}

/** One executed query of a cell (growth.query_runs joined to growth.queries). */
export interface CellQueryRun {
  queryRunId: number;
  scanRunId: number | null;
  queryText: string;
  family: string;
  source: string;
  templateCode: string | null;
  provider: string;
  executedAt: Instant;
  status: string;
  resultCount: number;
  newCandidates: number;
  costMicroUsd: number;
  errorCode: string | null;
}

/** GET /leads/cells/{cellId}: the cell card (v0.1.71). */
export interface CellDetail {
  asOf: Instant;
  cell: LeadMapCell;
  /** Newest first. */
  runs: CellRun[];
  /** Newest first, at most 200. */
  queries: CellQueryRun[];
  /** The companies found in the cell (same rows as the list). */
  companies: LeadListItem[];
}

/** Lead stages in funnel order (growth.leads.stage). */
export const LEAD_STAGES = ['bulundu', 'nitelikli', 'dogrulandi', 'temas', 'cevap', 'musteri', 'uygun_degil'] as const;
export type LeadStage = (typeof LEAD_STAGES)[number];

export const LEAD_STAGE_LABELS: Readonly<Record<LeadStage, string>> = Object.freeze({
  bulundu: 'Bulundu',
  nitelikli: 'Nitelikli',
  dogrulandi: 'Doğrulandı',
  temas: 'Temas',
  cevap: 'Cevap',
  musteri: 'Müşteri',
  uygun_degil: 'Uygun değil',
});

export interface FunnelStage {
  stage: LeadStage;
  leads: number;
  manualContactOnly: number;
  avgFitScore: number | null;
  avgConfidence: number | null;
  oldestInStageSince: Instant | null;
}

/** One region of config/regions.yaml with its numbers (GET /leads world). */
export interface WorldRegion {
  code: string;
  name: string;
  countries: string[];
  wave: number;
  priority: number;
  locale: string;
  labelAt: { lat: number; lon: number } | null;
  note: string | null;
  enabled: boolean;
  cells: number;
  scanned: number;
  partial: number;
  scanning: number;
  leads: number;
  qualified: number;
  customers: number;
  lastScannedAt: Instant | null;
}

export interface WorldCountry {
  code: string;
  regionCode: string | null;
  cells: number;
  scanned: number;
  leads: number;
  qualified: number;
  orders: number;
  /** growth.country_rules rule_code of the latest version, null when the country has no rule yet. */
  contactRule: string | null;
}

export interface WorldView {
  weeklyCellQuota: number;
  rescanDays: number;
  costPerCellUsd: number;
  regions: WorldRegion[];
  countries: WorldCountry[];
}

export interface LeadsView {
  /** Server time the numbers were read. */
  asOf: Instant;
  /** Owner regions with cells, in display order ("AU-QLD"). */
  regions: string[];
  cells: LeadMapCell[];
  /** Every stage listed, zero included, in LEAD_STAGES order. */
  funnel: FunnelStage[];
  /** Every lead with its company and primary site, best fit first (the list and the pins). */
  companies: LeadListItem[];
  /** Query totals per discovery provider and region (v0.1.79 dashboard tiles). */
  sources: SourceStat[];
  /** Per region: the saturation curve and the Chao1 coverage estimate (v0.1.80). */
  curves: ScanCurve[];
  /** The world plan: regions with their numbers and the countries (v0.1.16). */
  world: WorldView;
}

/** One point of a region's saturation curve: after n query runs, this many distinct companies. */
export interface CurvePoint {
  n: number;
  companies: number;
}

/** A region's saturation curve with the Chao1 estimate (f1 companies seen once, f2 seen twice). */
export interface ScanCurve {
  region: string;
  points: CurvePoint[];
  observed: number;
  f1: number;
  f2: number;
  estimate: number;
  /** observed / estimate, 0..1. */
  coverage: number;
}

/** One provider's totals in one region (growth.query_runs grouped). */
export interface SourceStat {
  region: string | null;
  provider: string;
  queries: number;
  results: number;
  newCandidates: number;
  costMicroUsd: number;
  cells: number;
}

/** The fit split of a lead (fit_vehicle 40, fit_market 20, fit_design 20, fit_size 10, fit_gap 10). */
export interface FitParts {
  vehicle: number;
  market: number;
  design: number;
  size: number;
  gap: number;
  total: number;
}

/** One row of the company list (growth.leads joined to its company and primary site). */
export interface LeadListItem {
  leadId: string;
  companyId: string;
  name: string;
  domain: string | null;
  locality: string | null;
  region: string | null;
  /** The geohash-5 cell of the primary site (the pilot's grid). */
  cellId: string | null;
  /** The H3 resolution 6 cell of the primary site (the wave-1 grid); pins and the cell filter use both. */
  h3r6: string | null;
  stage: LeadStage;
  fit: FitParts;
  source: string;
  /** The discovery providers whose results resolved to this company, sorted (may be empty for older rows). */
  providers: string[];
  lat: number | null;
  lon: number | null;
  ownerRegion: string | null;
  firstSeenAt: Instant;
}

export interface LeadEvidence {
  evidenceId: string;
  kind: string;
  claim: string;
  polarity: 'positive' | 'negative' | 'neutral';
  url: string | null;
  domain: string | null;
  observedOn: Instant | null;
  extracted: Record<string, unknown>;
}

/** What the pilot recorded about the company. */
export interface PilotSummary {
  makes: string | null;
  ourMatch: string | null;
  tier: string | null;
  ownManufacturing: string | null;
  productCategories: string[];
  vehicles: string[];
  locationRole: string | null;
  locationConfidence: string | null;
  pagesRead: string[];
  stageReason: string | null;
}

export interface LeadStageChange {
  fromStage: LeadStage | null;
  toStage: LeadStage;
  changedAt: Instant;
  actor: string;
}

/** GET /leads/{leadId}: the company card. */
export interface LeadDetail extends LeadListItem {
  website: string | null;
  stageChangedAt: Instant;
  unfitReason: string | null;
  relevanceReason: string | null;
  sourceRef: string | null;
  summary: PilotSummary | null;
  evidence: LeadEvidence[];
  history: LeadStageChange[];
}

/** A basemap geometry as the worker stores it: [lon, lat] pairs, five decimals. */
export type BaseGeometry =
  | { type: 'LineString'; coordinates: [number, number][] }
  | { type: 'Polygon'; coordinates: [number, number][][] };

export interface BaseFeature {
  geometry: BaseGeometry;
  name?: string;
}

export type BasemapKind = 'water' | 'coastline' | 'road' | 'industrial';

export interface BasemapLayer {
  kind: BasemapKind;
  featureCount: number;
  fetchedAt: Instant;
  features: BaseFeature[];
}

/** GET /leads/basemap/{region} (v0.1.81): OSM water, coastline, main roads and industrial areas under the cells. */
export interface Basemap {
  asOf: Instant;
  region: string;
  /** The box the layers were fetched for; null when the region has no layers yet. */
  bbox: { south: number; west: number; north: number; east: number } | null;
  layers: BasemapLayer[];
}
